import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AiRuntimeConfigManager from "../components/settings/AiRuntimeConfigManager";
import DeveloperPanel from "../components/settings/DeveloperPanel";
import WorkdaysPicker from "../components/settings/WorkdaysPicker";
import Button from "../components/common/Button";
import PageHeader from "../components/common/PageHeader";
import RetryBlock from "../components/common/RetryBlock";
import { useAuth } from "../context/AuthContext";
import useApiRequest from "../hooks/useApiRequest";
import useShellContext from "../layout/useShellContext";
import {
  getSettingsData,
  getSubscriptionSettings,
  createRechargeCheckout,
  createUpgradeCheckout,
  resetSettingsData,
  saveSettingsData,
} from "../services/settingsApi";
import { normalizeApiError } from "../utils/errors";
import { formatNow } from "../utils/formatters";
import { isAdminUser } from "../utils/roleAccess";

function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function formatCurrencyAed(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return "--";
  }

  return `AED ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed)}`;
}

function formatPlanDate(value) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function PlanSettingsPanel({ showToast }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadSubscription = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    setErrorMessage("");

    try {
      const data = await getSubscriptionSettings({ signal });
      setSubscription(data || null);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        setErrorMessage(normalizedError.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSubscription({ signal: controller.signal }).catch(() => undefined);

    return () => controller.abort();
  }, [loadSubscription]);

  const currentPlan = subscription?.current_plan || {};
  const usage = subscription?.usage || {};
  const features = Array.isArray(currentPlan.features) ? currentPlan.features : [];
  const consumedPercent = Number(usage.consumed_percent || 0);
  const boundedPercent = Math.min(100, Math.max(0, consumedPercent));
  const isActive = String(currentPlan.billing_status || "").toLowerCase() === "active";
  const statusLabel = isActive ? "Active" : "Expired";
  const nextTier = subscription?.next_tier || null;

  const handleRecharge = async () => {
    setActionBusy("recharge");
    try {
      const response = await createRechargeCheckout({});
      showToast(response?.message || "Recharge request created.", false, 5000);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      setActionBusy("");
    }
  };

  const handleUpgrade = async () => {
    if (!nextTier?.plan_name) {
      showToast("You are already on the highest available plan.", false, 4000);
      return;
    }

    setActionBusy("upgrade");
    try {
      const response = await createUpgradeCheckout({ plan_name: nextTier.plan_name });
      showToast(response?.message || "Upgrade request created.", false, 5000);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      setActionBusy("");
    }
  };

  return (
    <section id="plan-settings" className="chart-card settings-panel settings-panel-span-full plan-settings-panel">
      <div className="settings-panel-header plan-settings-header">
        <div>
          <div className="team-card-heading">Plan Settings</div>
          <div className="page-subtitle">
            View your current subscription, minutes usage, and included features.
          </div>
        </div>
        <Button disabled={loading} onClick={() => loadSubscription().catch(() => undefined)}>
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="loading-state compact">
          <div className="loading-spinner" />
          <div className="loading-title">Loading plan...</div>
        </div>
      ) : errorMessage ? (
        <RetryBlock
          message={errorMessage || "Plan settings are unavailable right now."}
          onRetry={() => loadSubscription().catch(() => undefined)}
        />
      ) : (
        <div className="plan-settings-layout">
          <div className="plan-settings-summary">
            <div className="plan-settings-title-row">
              <div>
                <span className="settings-label">Current Plan</span>
                <h3>{currentPlan.plan_name || "Plan not configured"}</h3>
              </div>
              <span className={`plan-status-pill ${isActive ? "is-active" : "is-expired"}`}>
                {statusLabel}
              </span>
            </div>

            <div className="plan-settings-meta-grid">
              <div>
                <span>Renewal</span>
                <strong>{formatPlanDate(currentPlan.renewal_date || usage.cycle_end)}</strong>
              </div>
              <div>
                <span>Price</span>
                <strong>{formatCurrencyAed(currentPlan.monthly_cost_aed)}/mo</strong>
              </div>
              <div>
                <span>Users</span>
                <strong>
                  {currentPlan.agent_limit
                    ? `${formatNumber(currentPlan.active_user_count)} / ${formatNumber(currentPlan.agent_limit)}`
                    : "--"}
                </strong>
              </div>
              <div>
                <span>Languages</span>
                <strong>{currentPlan.language_limit ? titleCase(currentPlan.language_limit) : "--"}</strong>
              </div>
            </div>

            <div className="plan-minutes-card">
              <div className="plan-minutes-copy">
                <div>
                  <span className="settings-label">Minutes Usage</span>
                  <strong>
                    {formatNumber(usage.used_minutes)} of {formatNumber(usage.allowance_minutes)} minutes used
                  </strong>
                </div>
                <span>{usage.remaining_minutes == null ? "--" : formatNumber(usage.remaining_minutes)} remaining</span>
              </div>
              <div className="plan-progress-track" aria-label="Plan minutes usage">
                <div className="plan-progress-fill" style={{ width: `${boundedPercent}%` }} />
              </div>
              <div className="plan-progress-foot">
                <span>{consumedPercent.toFixed(consumedPercent % 1 === 0 ? 0 : 1)}% consumed</span>
                <span>Cycle ends {formatPlanDate(usage.cycle_end)}</span>
              </div>
            </div>

            <div className="page-actions plan-settings-actions">
              <Button
                variant="primary"
                loading={actionBusy === "upgrade"}
                disabled={Boolean(actionBusy)}
                onClick={handleUpgrade}
              >
                Upgrade Plan
              </Button>
              <Button
                loading={actionBusy === "recharge"}
                disabled={Boolean(actionBusy)}
                onClick={handleRecharge}
              >
                Recharge More Minutes
              </Button>
            </div>
          </div>

          <div className="plan-features-card">
            <div className="team-card-heading">Plan Features</div>
            {features.length ? (
              <div className="plan-feature-list">
                {features.map((feature, index) => {
                  const included = Boolean(feature.included);
                  return (
                    <div
                      key={`${feature.key || feature.label || index}`}
                      className={`plan-feature-row ${included ? "is-included" : "is-locked"}`}
                    >
                      <span className="plan-feature-state">{included ? "OK" : "LOCK"}</span>
                      <div>
                        <strong>{feature.label || "Plan feature"}</strong>
                        {!included && feature.required_tier ? (
                          <small>Requires {feature.required_tier}</small>
                        ) : feature.description ? (
                          <small>{feature.description}</small>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="settings-inline-help">No feature list is configured for this plan.</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const location = useLocation();
  const { registerPageControls, resetPageControls, showToast } = useShellContext();
  const { user } = useAuth();
  const { data, error, run } = useApiRequest(null);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const mountedRef = useRef(false);

  const loadPageData = useCallback(async ({ signal } = {}) => {
    return getSettingsData({ signal });
  }, []);

  const loadSettingsPage = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      try {
        const result = await run(loadPageData);

        if (mountedRef.current) {
          setLastUpdated(formatNow());
        }

        return result;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [loadPageData, run],
  );

  const handleRefresh = useCallback(
    () => loadSettingsPage({ trackRefresh: true }),
    [loadSettingsPage],
  );

  useEffect(() => {
    mountedRef.current = true;
    loadSettingsPage().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadSettingsPage]);

  useEffect(() => {
    if (data) {
      setDraft(data);
      setBaseline(data);
    }
  }, [data]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [handleRefresh, lastUpdated, registerPageControls, resetPageControls, shellRefreshing]);

  useEffect(() => {
    if (!draft || !location.hash) {
      return;
    }

    const targetId = location.hash.replace("#", "");
    const target = document.getElementById(targetId);

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [draft, location.hash]);

  const updateSection = (section, key, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [key]: value,
      },
    }));
  };

  const isAdmin = isAdminUser(user);
  const isDirty =
    draft && baseline ? JSON.stringify(draft) !== JSON.stringify(baseline) : false;

  return (
    <>
      {error ? (
        <RetryBlock
          message="Settings are unavailable right now."
          onRetry={() => handleRefresh().catch(() => undefined)}
        />
      ) : null}

      <PageHeader
        title="Settings"
        subtitle="Manage your account, calling schedule, AI agents, and plan."
        actions={
          <>
            <Button
              variant="gold"
              disabled={busy || !draft || !isDirty}
              onClick={async () => {
                if (!draft) return;
                setBusy(true);
                try {
                  const response = await saveSettingsData(draft);
                  setDraft(response);
                  setBaseline(response);
                  setLastUpdated(formatNow());
                  showToast("Settings saved successfully.", false, 4000);
                } catch (errorValue) {
                  const normalizedError = normalizeApiError(errorValue);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true, 5000);
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save Changes
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const response = await resetSettingsData();
                  setDraft(response);
                  setBaseline(response);
                  showToast("Settings reset to the defaults.", false, 4000);
                } catch (errorValue) {
                  const normalizedError = normalizeApiError(errorValue);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true, 5000);
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              Reset Defaults
            </Button>
          </>
        }
      />

      {!draft ? (
        <div className="loading-state compact">
          <div className="loading-spinner" />
          <div className="loading-title">Loading settings...</div>
          <div className="loading-subtitle">
            Fetching account preferences and plan details.
          </div>
        </div>
      ) : (
        <div className="settings-grid">
          <PlanSettingsPanel showToast={showToast} />

          {/* Account Preferences */}
          <section id="account" className="chart-card settings-panel">
            <div className="settings-panel-header">
              <div className="team-card-heading">Account Preferences</div>
              <div className="page-subtitle">
                Your account identity and default landing page.
              </div>
            </div>
            <div className="settings-fields">
              <label className="settings-field">
                <span className="settings-label">Display Name</span>
                <input
                  className="settings-input"
                  value={draft.account.name}
                  onChange={(e) => updateSection("account", "name", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">Email</span>
                <input
                  className="settings-input"
                  value={draft.account.email}
                  onChange={(e) => updateSection("account", "email", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">Default Dashboard Page</span>
                <select
                  className="settings-select"
                  value={draft.account.defaultDashboard}
                  onChange={(e) => updateSection("account", "defaultDashboard", e.target.value)}
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="calls">Calls</option>
                  <option value="analytics">Analytics</option>
                  <option value="team-management">Team Management</option>
                </select>
              </label>
            </div>
          </section>

          {/* Calling Window Settings */}
          <section id="calling-window" className="chart-card settings-panel">
            <div className="settings-panel-header">
              <div className="team-card-heading">Calling Window Settings</div>
              <div className="page-subtitle">
                Define when outbound AI calls are permitted to run.
              </div>
            </div>
            <div className="settings-fields">
              <label className="settings-field">
                <span className="settings-label">Timezone</span>
                <select
                  className="settings-select"
                  value={draft.callingWindow.timezone}
                  onChange={(e) => updateSection("callingWindow", "timezone", e.target.value)}
                >
                  <option value="Asia/Dubai">Asia/Dubai (GST +4)</option>
                  <option value="Asia/Calcutta">Asia/Kolkata (IST +5:30)</option>
                  <option value="UTC">UTC</option>
                </select>
              </label>
              <div className="settings-field">
                <span className="settings-label">Working Days</span>
                <WorkdaysPicker
                  value={draft.callingWindow.workdays}
                  onChange={(next) => updateSection("callingWindow", "workdays", next)}
                  disabled={busy}
                />
              </div>
              <label className="settings-field">
                <span className="settings-label">Start Time</span>
                <input
                  type="time"
                  className="settings-input"
                  value={draft.callingWindow.startTime}
                  onChange={(e) => updateSection("callingWindow", "startTime", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">End Time</span>
                <input
                  type="time"
                  className="settings-input"
                  value={draft.callingWindow.endTime}
                  onChange={(e) => updateSection("callingWindow", "endTime", e.target.value)}
                />
              </label>
            </div>
          </section>

          {/* Admin-only sections */}
          {isAdmin ? <AiRuntimeConfigManager showToast={showToast} /> : null}
          {isAdmin ? <DeveloperPanel showToast={showToast} /> : null}
        </div>
      )}
    </>
  );
}
